import { resolveNodeTranslation } from "./workflowTranslations.js";

export const tagColorMapping = {
    "#F5222D": "red",
    "#d4380d": "volcano",
    "#FA8C16": "orange",
    "#FAAD14": "gold",
    "#84CC16": "lime",
    "#50A248": "green",
    "#13C2C2": "cyan",
    "#0A99FE": "blue",
    "#141BCB": "blue",
    "#722ED1": "purple",
    "#EB2F96": "magenta"
}

// Statuses a request can carry outside of the workflow's own steps.
export const staticRequestStatuses = [
    { label: 'Draft', value: 'draft' },
    { label: 'Archived', value: 'archived' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Deleted', value: 'deleted' }
]

// Every status a request can be in: the static ones above plus each workflow step
// it can sit on (approval steps and the end node). Shared by the advance-search
// status field and the Status column filter so both offer the same set.
//
// Labels are shown in the viewer's language — step names through the node's own
// stored translations, the static ones through i18n — while `value` stays the
// canonical status stored on the request, so filtering is unaffected.
export const getWorkflowStatusOptions = (workflow, { language, t } = {}) => {
    const translate = typeof t === "function" ? t : (label) => label
    const nodes = Array.isArray(workflow?.node) ? workflow.node : []
    const stepStatuses = nodes
        .filter(node => node.type === "default" || node.type === "textInput" || node.data?.type === "end")
        .map(node => ({
            label: resolveNodeTranslation(node.data, language)?.name || node.data?.name,
            value: node.data?.name
        }))
        .filter(option => option.value)

    const options = staticRequestStatuses
        .map(status => ({ ...status, label: translate(status.label) }))
        .concat(stepStatuses)
    // A step name can repeat a static status (e.g. a "Rejected" end node)
    return options.filter((option, index) => options.findIndex(o => o.value === option.value) === index)
}

// A request's status as shown to the viewer, the same way the space's request
// list shows it: a workflow step through the node's stored translations, anything
// else (draft, archived, …) through i18n, whose keys are capitalized ("Draft")
// while stored values are not. The canonical status is still what colors and
// matching run on.
//
// `workflow` is either the snapshot a request carries (`originalWorkflow`, steps
// under `nodes`) or a workflow document fetched on its own (steps under `node`).
export const getStatusDisplayLabel = (status, workflow, { language, t } = {}) => {
    if (!status) return status
    const nodes = workflow?.nodes || workflow?.node || []
    const step = nodes.find(node => node?.data?.name === status)
    if (step) return resolveNodeTranslation(step.data, language)?.name || status
    const text = String(status)
    return typeof t === "function" ? t(`${text.charAt(0).toUpperCase()}${text.slice(1)}`) : text
}

export const checkIfConnectorIsFromNoCondition = (nodeId, nodes=[], edges=[]) => {
    let conditionNode = nodes.find(node => node.id === nodeId);
    let stepPrevEdge = edges.find(edge => edge.target === nodeId)

}

const getConditionNodeOfCurrentNode = (nodeId, nodes, edges) => {
    let currentNode = nodes.find(node => node.id === nodeId)
    if(currentNode){
        let prevEdge = edges.find(edge => edge.target === nodeId);
        let prevNode = nodes.find(node => node.id === prevEdge.source)

        if(prevNode.data?.type === 'condition'){
            return prevNode
        } else {
            getConditionNodeOfCurrentNode(prevNode.id, nodes, edges)
        }
    }
}

const getYesNodes = (yesEdge, nodes, edges, reqNodes=[]) => {
    let nodeOfYes = nodes.find(node => node.id === yesEdge.target)
    if(nodeOfYes?.type !== 'circle'){
        reqNodes.push(nodeOfYes)
        let nextEdge = edges.find(edge => edge.source === nodeOfYes.id)
        if(nextEdge){
            getYesNodes(nextEdge, nodes, edges, reqNodes)
        }
    }
    return reqNodes
} 

export const getMergeNodeItems = (nodeId, nodes=[], edges=[]) => {
    let conditionNode = getConditionNodeOfCurrentNode(nodeId, nodes, edges);
    let conditionYesEdge = edges.find(edge => edge.source === conditionNode.id && edge.sourceHandle === "conditiona")
    const finalMergeNodes = getYesNodes(conditionYesEdge, nodes, edges, [])
}

export const getPreviousFormsForConditions = (nodes, edges, currentNode) => {
    const previousNodes = [];

    function traverse(nodeId) {
        let currentToPrevNodeEgde = edges.find(edge => edge.target === nodeId)
        if (currentToPrevNodeEgde) {
            let prevNode = nodes.find(node => node.id === currentToPrevNodeEgde.source)
            previousNodes.push(prevNode); // Add to path
            traverse(prevNode.id); // Recursive call to parent
        }
    }

    traverse(currentNode.id);
    return previousNodes;
}

export const checkNodeCondition = (prevNode, workflowConditions = [], prevCurrentEdge ) => {
    let hasCondition = true;
    
    // Check if prevNode exists and has the required properties before accessing them
    if(!prevNode || !prevNode.type || !prevNode.data) {
        return hasCondition; // Return true if prevNode is undefined to avoid blocking the workflow
    }
    
    if(prevNode.type === "circle" && prevNode.data && prevNode.data.type === "condition") {
        let nodeConditions = workflowConditions.filter(condition => condition.nodeId === prevNode.id)
        if(nodeConditions.length === 0) {
            hasCondition = false;           
        }
    }
    return hasCondition
}

// ---------------------------------------------------------------------------
// Parallel region (fork / join) helpers
// ---------------------------------------------------------------------------

// Walk upstream from a node to find the parallelGroupId governing its branch.
export const findParallelGroupId = (nodeId, nodes = [], edges = []) => {
    const startNode = nodes.find(n => n.id === nodeId)
    if (startNode?.data?.parallelGroupId) return startNode.data.parallelGroupId
    const visited = new Set()
    const walk = (id) => {
        if (visited.has(id)) return null
        visited.add(id)
        const incoming = edges.filter(e => e.target === id)
        for (const e of incoming) {
            if (e.data?.parallelGroupId) return e.data.parallelGroupId
            const src = nodes.find(n => n.id === e.source)
            if (src?.data?.parallelGroupId && src?.data?.type !== "join") return src.data.parallelGroupId
            const found = walk(e.source)
            if (found) return found
        }
        return null
    }
    return walk(nodeId)
}

// Find the branch tag ({parallelGroupId, branchId, branchLabel}) for the chain a node sits on,
// by walking upstream to the nearest edge that carries branch metadata.
export const findBranchInfo = (nodeId, nodes = [], edges = []) => {
    const visited = new Set()
    const walk = (id) => {
        if (visited.has(id)) return null
        visited.add(id)
        const incoming = edges.filter(e => e.target === id)
        for (const e of incoming) {
            if (e.data?.branchId) {
                return { parallelGroupId: e.data.parallelGroupId, branchId: e.data.branchId, branchLabel: e.data.branchLabel }
            }
            const found = walk(e.source)
            if (found) return found
        }
        return null
    }
    return walk(nodeId)
}

export const findJoinNodeForGroup = (groupId, nodes = []) =>
    nodes.find(n => n.data?.type === "join" && n.data?.parallelGroupId === groupId)

// Distribute a fork's parallel branches symmetrically above/below the fork's Y so the
// fork (and the join, which sits at the fork's Y) stay vertically centered. Each branch's
// whole sub-flow is shifted together, stopping at the join boundary.
export const relayoutParallelBranches = (forkId, nodes = [], edges = [], gap = 110) => {
    const fork = nodes.find(n => n.id === forkId)
    if (!fork) return nodes
    const groupId = fork.data?.parallelGroupId
    const join = groupId ? findJoinNodeForGroup(groupId, nodes) : null
    const joinId = join?.id

    const branchEdges = edges.filter(e => e.source === fork.id)
    if (branchEdges.length < 2) return nodes

    // For each branch, collect its sub-flow node ids (forward BFS, stopping at the join).
    const branches = branchEdges.map(be => {
        const first = nodes.find(n => n.id === be.target)
        const ids = new Set()
        const queue = [be.target]
        while (queue.length) {
            const id = queue.shift()
            if (id === joinId || ids.has(id)) continue
            ids.add(id)
            edges.filter(e => e.source === id).forEach(oe => {
                if (oe.target !== joinId) queue.push(oe.target)
            })
        }
        return { first, ids }
    }).filter(b => b.first)

    // Stable order by current vertical position.
    branches.sort((a, b) => a.first.position.y - b.first.position.y)

    const n = branches.length
    const shiftById = new Map()
    branches.forEach((b, i) => {
        const targetY = fork.position.y + (i - (n - 1) / 2) * gap
        const delta = targetY - b.first.position.y
        if (delta !== 0) {
            b.ids.forEach(id => shiftById.set(id, (shiftById.get(id) || 0) + delta))
        }
    })

    const positioned = nodes.map(node =>
        shiftById.has(node.id)
            ? { ...node, position: { x: node.position.x, y: node.position.y + shiftById.get(node.id) } }
            : node
    )

    // Safety net: a branch that is just a bare "+" connector (no step built yet) can land
    // in the step column and hide behind a step card. Nudge such connectors clear so they
    // stay visible. Their sub-flow is only the connector itself, so moving it is safe.
    const isBareConnector = (node) => node?.type === "circle" && !node?.data?.type
    const stepNodes = positioned.filter(node => node.type === "textInput")
    const overlapsStep = (pos) => stepNodes.some(s => Math.abs(s.position.x - pos.x) < 120 && Math.abs(s.position.y - pos.y) < 60)

    const bareEntryIds = new Set(branches.filter(b => b.ids.size === 1 && isBareConnector(b.first)).map(b => b.first.id))
    return positioned.map(node => {
        if (!bareEntryIds.has(node.id)) return node
        let pos = { ...node.position }
        let guard = 0
        while (overlapsStep(pos) && guard < 12) {
            pos = { x: pos.x, y: pos.y + 40 }
            guard++
        }
        return pos.y !== node.position.y ? { ...node, position: pos } : node
    })
}

export const findForkNodeForGroup = (groupId, nodes = []) =>
    nodes.find(n => n.data?.parallelGroupId === groupId && n.data?.type !== "join")

// Collect every node/edge of a parallel region for deletion. Triggered when the
// user deletes the fork or the join. Returns the ids to remove plus where to
// reconnect the surviving flow so nothing is orphaned.
export const getParallelRegionToDelete = (node, nodes = [], edges = []) => {
    const groupId = node?.data?.parallelGroupId || findParallelGroupId(node.id, nodes, edges)
    if (!groupId) return null
    const fork = findForkNodeForGroup(groupId, nodes)
    const join = findJoinNodeForGroup(groupId, nodes)
    if (!fork) return null

    const delNodes = new Set()
    const delEdges = new Set()

    const branchEdges = edges.filter(e => e.source === fork.id && e.data?.parallelGroupId === groupId)
    branchEdges.forEach(be => {
        delEdges.add(be.id)
        const queue = [be.target]
        while (queue.length) {
            const id = queue.shift()
            if (join && id === join.id) continue   // stop at the join boundary
            if (delNodes.has(id)) continue
            delNodes.add(id)
            edges.filter(e => e.source === id).forEach(oe => {
                delEdges.add(oe.id)
                if (!(join && oe.target === join.id)) queue.push(oe.target)
            })
        }
    })

    let continuationTarget = null
    if (join) {
        delNodes.add(join.id)
        const joinOut = edges.find(e => e.source === join.id)
        if (joinOut) {
            delEdges.add(joinOut.id)
            continuationTarget = joinOut.target
        }
        edges.filter(e => e.target === join.id).forEach(e => delEdges.add(e.id))
    }

    const removeFork = node.id === fork.id
    let reconnectFromNodeId = fork.id
    if (removeFork) {
        delNodes.add(fork.id)
        const inFork = edges.find(e => e.target === fork.id)
        reconnectFromNodeId = inFork ? inFork.source : null
        if (inFork) delEdges.add(inFork.id)
    }

    return {
        nodeIds: [...delNodes],
        edgeIds: [...delEdges],
        reconnectFromNodeId,
        continuationTarget,
        groupId,
        removeFork
    }
}

export function sortNodesByEdges(nodes, edges) {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  const graph = {};
  const inDegree = {};

  nodes.forEach(n => {
    graph[n.id] = [];
    inDegree[n.id] = 0;
  });

  edges.forEach(e => {
    graph[e.source].push(e.target);
    inDegree[e.target] += 1;
  });

  const queue = Object.keys(inDegree).filter(id => inDegree[id] === 0);
  const sorted = [];

  while (queue.length) {
    const id = queue.shift();
    sorted.push(nodeMap.get(id));

    graph[id].forEach(next => {
      inDegree[next] -= 1;
      if (inDegree[next] === 0) queue.push(next);
    });
  }

  return sorted;
}